import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  addDoc,
  updateDoc,
  setDoc,
  doc,
  getDoc,
  arrayUnion,
  arrayRemove,
  increment,
  serverTimestamp,
  getDocs,
} from 'firebase/firestore';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db, auth } from '@/config/firebase';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';
import { attachLinkPreviewToContent } from '@/services/chatPreview.service';
import { shouldAdvanceReadReceipt } from '@/lib/chatUnread';
import type {
  Message,
  SendMessageData,
  TypingUser,
  UseGroupChatReturn,
} from '@flock-types/chat.types';

/**
 * Options for useGroupChat hook
 */
export interface UseGroupChatOptions {
  /** The group ID to subscribe to */
  groupId: string;
  /** The group name (used for push notification titles) */
  groupName?: string;
  /** Number of messages to load per page (default: 50) */
  pageSize?: number;
  /** Optional chat group ID. When set, queries messages for that chat group.
   *  When null/undefined, queries "Everyone" messages (no chatGroupId). */
  chatGroupId?: string | null;
  /** The chat group name (used for push notification titles when chatGroupId is set) */
  chatGroupName?: string;
  /** When set, triggers fetching older messages around this ID if not in the loaded set */
  scrollToMessageId?: string | null;
  /** When true, render the fetched context window as the active list while opening the target */
  anchorScrollToMessage?: boolean;
  /** When false, suppresses read receipt writes. Use when the hook runs for data loading
   *  but the user isn't actively viewing the conversation (e.g., on the conversation list). */
  writeReceipts?: boolean;
}

// Firestore collection names
const MESSAGES_COLLECTION = 'messages';
const READ_RECEIPTS_COLLECTION = 'read_receipts';

// Typing indicator constants
const TYPING_DEBOUNCE_MS = 300;
const TYPING_TIMEOUT_MS = 3000;

/**
 * Convert Firestore document to Message type
 */
function docToMessage(doc: QueryDocumentSnapshot<DocumentData>): Message {
  const data = doc.data();
  return {
    id: doc.id,
    organizationId: data.organizationId,
    groupId: data.groupId,
    chatGroupId: data.chatGroupId || null,
    userId: data.userId,
    userName: data.userName,
    userProfileImageUrl: data.userProfileImageUrl,
    type: data.type || 'text',
    content: data.content || {},
    replyTo: data.replyTo,
    mentions: data.mentions || [],
    mentionedUserIds: data.mentionedUserIds || [],
    reactions: data.reactions || {},
    reactionCounts: data.reactionCounts || {},
    isPinned: data.isPinned || false,
    pinnedAt: data.pinnedAt,
    pinnedBy: data.pinnedBy,
    isDeleted: data.isDeleted || false,
    isEdited: data.isEdited || false,
    editedAt: data.editedAt,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

function getMessageCreatedAtMillis(message: Message): number {
  const timestamp = message.createdAt;
  if (!timestamp) return 0;
  if (typeof timestamp === 'object') {
    if ('toMillis' in timestamp && typeof timestamp.toMillis === 'function') {
      return timestamp.toMillis();
    }
    if ('toDate' in timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().getTime();
    }
  }
  const parsed = new Date(timestamp as string).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function sortMessagesNewestFirst(messages: Message[]): Message[] {
  return [...messages].sort((a, b) => getMessageCreatedAtMillis(b) - getMessageCreatedAtMillis(a));
}

/**
 * Hook for real-time group chat functionality.
 *
 * Features:
 * - Real-time Firestore subscription for messages
 * - Separate subscription for pinned messages
 * - Optimistic updates for send/edit/delete/reactions
 * - Typing indicators
 * - Pagination support with loadMore
 * - Proper cleanup on unmount
 */
export type UseGroupChatWebReturn = UseGroupChatReturn & {
  /** Web addition: leader pin/unpin via direct Firestore update. */
  setPinned: (messageId: string, pinned: boolean) => Promise<void>;
};

export function useGroupChat(options: UseGroupChatOptions): UseGroupChatWebReturn {
  const {
    groupId,
    groupName,
    pageSize = 50,
    chatGroupId,
    chatGroupName,
    scrollToMessageId,
    anchorScrollToMessage = false,
    writeReceipts = true,
  } = options;
  const { user, currentOrganization } = useAuth();

  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [scrollContextMessages, setScrollContextMessages] = useState<Message[]>([]);
  const [activeScrollContextTargetId, setActiveScrollContextTargetId] = useState<string | null>(null);
  const [pinnedMessages, setPinnedMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup and state tracking
  const mountedRef = useRef(true);
  const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const writeReceiptsRef = useRef(writeReceipts);
  writeReceiptsRef.current = writeReceipts;
  const anchorScrollToMessageRef = useRef(anchorScrollToMessage);
  anchorScrollToMessageRef.current = anchorScrollToMessage;
  const isTypingRef = useRef(false);
  const typingUsersTimeoutRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const subscriptionGenRef = useRef(0);
  const lastFetchedScrollIdRef = useRef<string | null>(null);
  const fetchingScrollIdRef = useRef<string | null>(null);
  const lastReadAtRef = useRef<string | null>(null);

  // Memoize organizationId to prevent unnecessary re-subscriptions
  const organizationId = useMemo(() => currentOrganization?.id, [currentOrganization?.id]);

  const clearScrollContext = useCallback(() => {
    lastFetchedScrollIdRef.current = null;
    fetchingScrollIdRef.current = null;
    setActiveScrollContextTargetId(null);
    setScrollContextMessages([]);
  }, []);

  const writeReadReceipt = useCallback(
    async (firebaseUid: string, lastReadAt: string, context: string) => {
      // Web-specific: never advance receipts from a hidden tab. A background
      // tab left on a conversation would otherwise mark messages as read the
      // user hasn't seen (mobile has no equivalent — a visible RN screen is
      // by definition foregrounded). The visibilitychange effect below writes
      // the receipt when the tab becomes visible again.
      if (context !== 'after send' && document.visibilityState !== 'visible') {
        return;
      }
      if (!organizationId || !shouldAdvanceReadReceipt(lastReadAtRef.current, lastReadAt)) {
        return;
      }

      lastReadAtRef.current = lastReadAt;

      const receiptDocId = chatGroupId
        ? `${firebaseUid}_${groupId}_${chatGroupId}`
        : `${firebaseUid}_${groupId}`;
      const receiptRef = doc(db, READ_RECEIPTS_COLLECTION, receiptDocId);

      await setDoc(receiptRef, {
        userId: firebaseUid,
        groupId,
        organizationId,
        ...(chatGroupId ? { chatGroupId } : {}),
        lastReadAt,
        updatedAt: new Date().toISOString(),
      }, { merge: true }).catch((err) => {
        console.warn(`[useGroupChat] Read receipt write (${context}) failed:`, err.message);
      });
    },
    [chatGroupId, groupId, organizationId]
  );

  const isScrollContextActive = useMemo(() => (
    Boolean(
      activeScrollContextTargetId &&
      scrollContextMessages.some((message) => message.id === activeScrollContextTargetId)
    )
  ), [activeScrollContextTargetId, scrollContextMessages]);

  // Search-result jumps use a bounded context window as the active list so
  // FlatList only has to land inside a small, already-fetched neighborhood.
  const displayedMessages = useMemo((): Message[] => {
    if (isScrollContextActive) return sortMessagesNewestFirst(scrollContextMessages);
    if (scrollContextMessages.length === 0) return messages;
    const merged = new Map(messages.map((m) => [m.id, m]));
    scrollContextMessages.forEach((m) => {
      if (!merged.has(m.id)) merged.set(m.id, m);
    });
    return sortMessagesNewestFirst(Array.from(merged.values()));
  }, [messages, scrollContextMessages, isScrollContextActive]);

  /**
   * Subscribe to messages in real-time
   */
  useEffect(() => {
    if (!groupId || !organizationId) {
      setLoading(false);
      return;
    }

    // Reset mounted ref
    mountedRef.current = true;
    setMessages([]);
    setLoading(true);
    setError(null);

    let unsubscribeMessages: (() => void) | null = null;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    function subscribeToMessages(retryCount = 0) {
      if (!mountedRef.current) return;

      // Build query for messages
      // Both branches filter by chatGroupId explicitly:
      // - Chat group: where('chatGroupId', '==', chatGroupId)
      // - Everyone: where('chatGroupId', '==', null)
      // This ensures Firestore security rules can validate every returned document.
      const messagesRef = collection(db, MESSAGES_COLLECTION);
      const queryConstraints = chatGroupId
        ? [
            where('chatGroupId', '==', chatGroupId),
            where('organizationId', '==', organizationId),
            where('isDeleted', '==', false),
            orderBy('createdAt', 'desc'),
            limit(pageSize),
          ]
        : [
            where('chatGroupId', '==', null),
            where('groupId', '==', groupId),
            where('organizationId', '==', organizationId),
            where('isDeleted', '==', false),
            orderBy('createdAt', 'desc'),
            limit(pageSize),
          ];
      const messagesQuery = query(messagesRef, ...queryConstraints);

      // Subscribe to messages
      unsubscribeMessages = onSnapshot(
        messagesQuery,
        (snapshot) => {
          if (!mountedRef.current) return;

          let newMessages: Message[] = [];
          snapshot.forEach((doc) => {
            newMessages.push(docToMessage(doc));
          });

          // Track last document for pagination
          if (snapshot.docs.length > 0) {
            lastDocRef.current = snapshot.docs[snapshot.docs.length - 1];
          }

          // Check if there might be more messages
          setHasMore(snapshot.docs.length === pageSize);

          setMessages(newMessages);
          setLoading(false);
          setIsSyncing(false);

          // Write read receipt — marks this conversation as read
          // Use the latest message's server timestamp to avoid client/server clock skew.
          // Both lastReadAt and lastMessageAt (set by Cloud Function) now derive from
          // Firestore server timestamps, eliminating the bug where device clock offset
          // caused sender's own messages to appear unread or badges to stick.
          const currentUid = auth.currentUser?.uid;
          if (writeReceiptsRef.current && newMessages.length > 0 && currentUid) {
            // Extract latest message's server timestamp (messages ordered desc).
            // If the latest message has a pending serverTimestamp (user just sent it),
            // skip the receipt write — sendMessage() will write it with the confirmed
            // server timestamp after addDoc resolves. Falling back to client time here
            // caused the sender's own message to be counted as unread when the device
            // clock was even slightly behind the Firestore server.
            const latestCreatedAt = newMessages[0]?.createdAt;
            let lastReadAt: string | null = null;
            if (latestCreatedAt && typeof latestCreatedAt === 'object' && (latestCreatedAt as any).toDate) {
              lastReadAt = (latestCreatedAt as any).toDate().toISOString();
            } else if (typeof latestCreatedAt === 'string') {
              lastReadAt = latestCreatedAt;
            }
            // If lastReadAt is still null, latest message has pending serverTimestamp.
            // Don't write receipt — sendMessage() handles this case.
            if (!lastReadAt) return;

            writeReadReceipt(currentUid, lastReadAt, 'snapshot');
          }
        },
        (error: any) => {
          const isPermissionError =
            error?.code === 'permission-denied' ||
            error?.message?.includes('Missing or insufficient permissions');

          if (isPermissionError && retryCount < 3) {
            // Firestore groupMembers doc may not be synced yet (race with login).
            // Retry after a delay to give the backend time to write it.
            const delay = (retryCount + 1) * 2000; // 2s, 4s, 6s
            console.log(`[useGroupChat] Permission denied, retrying in ${delay}ms (attempt ${retryCount + 1}/3)`);
            if (unsubscribeMessages) {
              unsubscribeMessages();
              unsubscribeMessages = null;
            }
            retryTimeout = setTimeout(() => {
              // Force token refresh before retry so Firestore sees latest auth state
              const currentUser = auth.currentUser;
              if (currentUser) {
                currentUser.getIdToken(true).then(() => {
                  subscribeToMessages(retryCount + 1);
                }).catch(() => {
                  subscribeToMessages(retryCount + 1);
                });
              } else {
                subscribeToMessages(retryCount + 1);
              }
            }, delay);
          } else if (isPermissionError) {
            console.log('[useGroupChat] Permission denied after retries - user may be pending approval');
            if (mountedRef.current) {
              setError('Unable to load chat. If you are a member of this group, try logging out and back in.');
              setLoading(false);
              setIsSyncing(false);
            }
          } else {
            console.error('[useGroupChat] Messages subscription error:', error);
            if (mountedRef.current) {
              setError('Unable to load messages. Please try again.');
              setLoading(false);
              setIsSyncing(false);
            }
          }
        }
      );
    }

    // Ensure Firebase token is fresh before subscribing.
    // The backend syncs groupMembers to Firestore on login, but the doc
    // may not exist yet if the app navigates to chat immediately.
    // Generation counter prevents stale async getIdToken from creating
    // a subscription after chatGroupId has already changed.
    const currentGen = ++subscriptionGenRef.current;

    (async () => {
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          await currentUser.getIdToken(true);
        }
      } catch (e) {
        console.warn('[useGroupChat] Token refresh failed, proceeding anyway:', e);
      }
      if (subscriptionGenRef.current !== currentGen) {
        console.log('[useGroupChat] Stale subscription detected after token refresh, skipping');
        return;
      }
      subscribeToMessages();
    })();

    // Cleanup on unmount or dependency change
    return () => {
      mountedRef.current = false;
      if (unsubscribeMessages) unsubscribeMessages();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
    // writeReceipts intentionally excluded: writeReceiptsRef.current is the
    // live source of truth inside the callback. Including it would tear down
    // and re-create the Firestore subscription on every conversation enter/exit,
    // wiping `messages` and causing a blank-screen flash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, organizationId, pageSize, chatGroupId, writeReadReceipt]);

  /**
   * Write a read receipt when writeReceipts transitions to true (user navigates
   * INTO a conversation). The onSnapshot callback uses writeReceiptsRef but won't
   * fire again if no new messages arrive — this effect ensures the receipt is
   * written immediately using the already-loaded messages.
   */
  useEffect(() => {
    if (!writeReceipts || messages.length === 0) return;
    const currentUid = auth.currentUser?.uid;
    if (!currentUid || !groupId || !organizationId) return;

    const latestCreatedAt = messages[0]?.createdAt;
    let lastReadAt: string;
    if (latestCreatedAt && typeof latestCreatedAt === 'object' && (latestCreatedAt as any).toDate) {
      lastReadAt = (latestCreatedAt as any).toDate().toISOString();
    } else if (typeof latestCreatedAt === 'string') {
      lastReadAt = latestCreatedAt;
    } else {
      lastReadAt = new Date().toISOString();
    }

    writeReadReceipt(currentUid, lastReadAt, 'on navigate');
  }, [writeReceipts, messages.length > 0, groupId, organizationId, chatGroupId, writeReadReceipt]); // Also fires when messages load (deep link: writeReceipts=true but messages empty on mount)

  /**
   * Web-specific: when the tab becomes visible while this conversation is
   * active, write the receipt for messages that arrived while hidden (the
   * hidden-tab guard in writeReadReceipt skipped them).
   */
  useEffect(() => {
    if (!writeReceipts) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const currentUid = auth.currentUser?.uid;
      if (!currentUid || messages.length === 0) return;
      const latestCreatedAt = messages[0]?.createdAt;
      let lastReadAt: string | null = null;
      if (latestCreatedAt && typeof latestCreatedAt === 'object' && (latestCreatedAt as any).toDate) {
        lastReadAt = (latestCreatedAt as any).toDate().toISOString();
      } else if (typeof latestCreatedAt === 'string') {
        lastReadAt = latestCreatedAt;
      }
      if (lastReadAt) writeReadReceipt(currentUid, lastReadAt, 'on visible');
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [writeReceipts, messages, writeReadReceipt]);

  /**
   * Subscribe to pinned messages separately
   */
  useEffect(() => {
    if (!groupId || !organizationId) {
      return;
    }

    // Build query for pinned messages
    // Both branches filter by chatGroupId explicitly.
    const messagesRef = collection(db, MESSAGES_COLLECTION);
    const pinnedConstraints = chatGroupId
      ? [
          where('chatGroupId', '==', chatGroupId),
          where('organizationId', '==', organizationId),
          where('isPinned', '==', true),
          where('isDeleted', '==', false),
          orderBy('pinnedAt', 'desc'),
        ]
      : [
          where('chatGroupId', '==', null),
          where('groupId', '==', groupId),
          where('organizationId', '==', organizationId),
          where('isPinned', '==', true),
          where('isDeleted', '==', false),
          orderBy('pinnedAt', 'desc'),
        ];
    const pinnedQuery = query(messagesRef, ...pinnedConstraints);

    // Subscribe to pinned messages
    const unsubscribePinned = onSnapshot(
      pinnedQuery,
      (snapshot) => {
        if (!mountedRef.current) return;

        let pinned: Message[] = [];
        snapshot.forEach((doc) => {
          pinned.push(docToMessage(doc));
        });

        setPinnedMessages(pinned);
      },
      (error: any) => {
        // Permission errors are expected for pending_approval users - don't log as error
        const isPermissionError =
          error?.code === 'permission-denied' ||
          error?.message?.includes('Missing or insufficient permissions');

        if (!isPermissionError) {
          console.error('[useGroupChat] Pinned messages subscription error:', error);
        }
      }
    );

    // Cleanup on unmount
    return () => {
      unsubscribePinned();
    };
  }, [groupId, organizationId, chatGroupId]);

  /**
   * Fetch messages around a scroll target when it's not in the loaded set.
   * Triggered by AI search "View in Chat" for messages older than the latest page.
   * Writes to scrollContextMessages (separate from subscription state) so
   * onSnapshot callbacks can't wipe out the fetched context.
   */
  useEffect(() => {
    if (!scrollToMessageId || !groupId || !organizationId || loading) return;

    const targetId = scrollToMessageId;
    const targetInMessages = messages.some((m) => m.id === targetId);
    const targetInContext = scrollContextMessages.some((m) => m.id === targetId);

    if (anchorScrollToMessage && targetInContext) {
      if (activeScrollContextTargetId !== targetId) {
        setActiveScrollContextTargetId(targetId);
      }
      lastFetchedScrollIdRef.current = targetId;
      return;
    }

    // Skip if we already fetched or are fetching this message ID
    if (lastFetchedScrollIdRef.current === targetId) return;
    if (fetchingScrollIdRef.current === targetId) return;

    // Check if message already exists in subscription messages
    if (!anchorScrollToMessage && targetInMessages) return;

    async function fetchAroundMessage(targetId: string) {
      fetchingScrollIdRef.current = targetId;

      const queryString = chatGroupId ? `?chatGroupId=${encodeURIComponent(chatGroupId)}` : '';
      const response = await api.get<{ success: boolean; data: { messages: Message[] } }>(
        `/groups/${groupId}/chat/messages/${encodeURIComponent(targetId)}/context${queryString}`
      );
      const fetchedMessages = response.data?.messages || [];

      if (!fetchedMessages.some((message) => message.id === targetId)) {
        console.warn('[useGroupChat] Scroll context response did not include target:', targetId);
        return;
      }

      // Store fetched messages in separate state (won't be overwritten by subscription).
      setScrollContextMessages(fetchedMessages);
      if (anchorScrollToMessageRef.current) {
        setActiveScrollContextTargetId(targetId);
      }
      lastFetchedScrollIdRef.current = targetId;
    }

    fetchAroundMessage(targetId)
      .catch((err) => console.error('[useGroupChat] Scroll fetch error:', err))
      .finally(() => {
        if (fetchingScrollIdRef.current === targetId) {
          fetchingScrollIdRef.current = null;
        }
      });
  }, [
    scrollToMessageId,
    anchorScrollToMessage,
    messages,
    scrollContextMessages,
    activeScrollContextTargetId,
    loading,
    groupId,
    organizationId,
    chatGroupId,
  ]);

  /**
   * Clear scroll context when conversation changes or scroll target is cleared
   */
  useEffect(() => {
    clearScrollContext();
  }, [groupId, chatGroupId, clearScrollContext]);

  /**
   * Cleanup typing indicator timeouts on unmount
   */
  useEffect(() => {
    const typingTimeout = typingTimeoutRef;
    const typingDebounce = typingDebounceRef;
    const typingUsersTimeouts = typingUsersTimeoutRef;

    return () => {
      if (typingTimeout.current) {
        clearTimeout(typingTimeout.current);
      }
      if (typingDebounce.current) {
        clearTimeout(typingDebounce.current);
      }
      typingUsersTimeouts.current.forEach((timeout) => clearTimeout(timeout));
      typingUsersTimeouts.current.clear();
    };
  }, []);

  /**
   * Load more messages (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || loading || !lastDocRef.current || !groupId || !organizationId) {
      return;
    }

    try {
      const messagesRef = collection(db, MESSAGES_COLLECTION);
      const moreConstraints = chatGroupId
        ? [
            where('chatGroupId', '==', chatGroupId),
            where('organizationId', '==', organizationId),
            where('isDeleted', '==', false),
            orderBy('createdAt', 'desc'),
            startAfter(lastDocRef.current),
            limit(pageSize),
          ]
        : [
            where('chatGroupId', '==', null),
            where('groupId', '==', groupId),
            where('organizationId', '==', organizationId),
            where('isDeleted', '==', false),
            orderBy('createdAt', 'desc'),
            startAfter(lastDocRef.current),
            limit(pageSize),
          ];
      const moreQuery = query(messagesRef, ...moreConstraints);

      const snapshot = await getDocs(moreQuery);

      if (!mountedRef.current) return;

      let olderMessages: Message[] = [];
      snapshot.forEach((doc) => {
        olderMessages.push(docToMessage(doc));
      });

      if (snapshot.docs.length > 0) {
        lastDocRef.current = snapshot.docs[snapshot.docs.length - 1];
      }

      setHasMore(snapshot.docs.length === pageSize);
      setMessages((prev) => [...prev, ...olderMessages]);
    } catch (error) {
      console.error('[useGroupChat] Load more error:', error);
    }
  }, [hasMore, loading, groupId, organizationId, pageSize, chatGroupId]);

  /**
   * Send a new message with optimistic update
   */
  const sendMessage = useCallback(
    async (data: SendMessageData) => {
      if (!user || !organizationId || !groupId) {
        throw new Error('User not authenticated or missing context');
      }

      clearScrollContext();

      // Get Firebase UID for Firestore security rules
      const firebaseUid = auth.currentUser?.uid;

      if (!firebaseUid) {
        throw new Error('Firebase user not authenticated');
      }

      const contentWithPreview = await attachLinkPreviewToContent(data.content);

      // Create optimistic message
      const optimisticId = `temp-${Date.now()}`;
      const optimisticMessage: Message = {
        id: optimisticId,
        organizationId,
        groupId,
        chatGroupId: chatGroupId || null,
        userId: firebaseUid,
        userName: user.name,
        userProfileImageUrl: user.profile_image_url,
        type: data.type,
        content: contentWithPreview,
        replyTo: data.replyTo,
        mentions: data.mentions || [],
        mentionedUserIds: data.mentions?.map((m) => m.userId) || [],
        reactions: {},
        reactionCounts: {},
        isPinned: false,
        isDeleted: false,
        isEdited: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Optimistic update - add to front of messages
      setMessages((prev) => [optimisticMessage, ...prev]);
      setPendingCount((prev) => prev + 1);

      try {
        // Force-refresh Firebase auth token for image messages (upload delay may cause stale token)
        if (data.type === 'image' || data.content?.mediaUrl) {
          try {
            const currentUser = auth.currentUser;
            if (currentUser) {
              await currentUser.getIdToken(true);
            }
          } catch (tokenErr) {
            console.warn('[useGroupChat] Token refresh before image send failed:', tokenErr);
          }
        }

        // Add message to Firestore
        const messagesRef = collection(db, MESSAGES_COLLECTION);
        const messageDoc: Record<string, any> = {
          organizationId,
          groupId,
          groupName: groupName || 'Group Chat', // For push notification titles
          userId: firebaseUid,
          userName: user.name,
          userProfileImageUrl: user.profile_image_url || null,
          type: data.type,
          content: contentWithPreview,
          replyTo: data.replyTo || null,
          mentions: data.mentions || [],
          mentionedUserIds: data.mentions?.map((m) => m.userId) || [],
          reactions: {},
          reactionCounts: {},
          isPinned: false,
          isDeleted: false,
          isEdited: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        // Always include chatGroupId so Firestore can query by it
        messageDoc.chatGroupId = chatGroupId || null;
        if (chatGroupId) {
          messageDoc.chatGroupName = chatGroupName || 'Chat Group';
        }
        const sentDocRef = await addDoc(messagesRef, messageDoc);

        // Fire-and-forget: write receipt with the confirmed server timestamp.
        // addDoc resolves after server acknowledgment, so the doc now has a real
        // createdAt. Reading it back gives us the exact server timestamp to store
        // in the receipt. This ensures the receipt always covers the sender's own
        // message, even if the user navigates away before onSnapshot fires with
        // the confirmed timestamp. Without this, the receipt could be stale (from
        // a previous message), causing the sender's own message to be counted as
        // unread by useAllGroupsUnread's count query.
        getDoc(sentDocRef).then(sentSnap => {
          const confirmedTs = sentSnap.data()?.createdAt;
          if (confirmedTs?.toDate) {
            writeReadReceipt(firebaseUid, confirmedTs.toDate().toISOString(), 'after send').catch(() => {});
          }
        }).catch(() => {});

        // Message will be updated by real-time listener
        // Note: chatGroups doc lastMessage* fields are updated by the Cloud Function
        // (client-side writes to chatGroups are blocked by Firestore security rules)
        setPendingCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error('[useGroupChat] sendMessage failed:', {
          type: data.type,
          hasMediaUrl: !!contentWithPreview?.mediaUrl,
          errorCode: (error as any)?.code,
          errorMessage: (error as any)?.message,
        });
        // Remove optimistic message on error
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
        setPendingCount((prev) => Math.max(0, prev - 1));
        throw error;
      }
    },
    [user, organizationId, groupId, groupName, chatGroupId, chatGroupName, writeReadReceipt, clearScrollContext]
  );

  /**
   * Edit an existing message
   */
  const editMessage = useCallback(
    async (messageId: string, newText: string) => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const message = displayedMessages.find((m) => m.id === messageId);
      const nextContent = await attachLinkPreviewToContent({
        ...(message?.content || {}),
        text: newText,
      });

      // Optimistic update
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                content: m.id === messageId ? nextContent : m.content,
                isEdited: true,
                editedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              }
            : m
        )
      );

      try {
        const messageRef = doc(db, MESSAGES_COLLECTION, messageId);
        await updateDoc(messageRef, {
          content: nextContent,
          isEdited: true,
          editedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        console.error('[useGroupChat] Edit message error:', error);
        throw error;
      }
    },
    [user, displayedMessages]
  );

  /**
   * Delete a message (soft delete)
   */
  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const firebaseUid = auth.currentUser?.uid;

      if (!firebaseUid) {
        throw new Error('Firebase user not authenticated');
      }

      // Optimistic update
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      setPinnedMessages((prev) => prev.filter((m) => m.id !== messageId));

      try {
        const messageRef = doc(db, MESSAGES_COLLECTION, messageId);
        await updateDoc(messageRef, {
          isDeleted: true,
          deletedAt: serverTimestamp(),
          deletedBy: firebaseUid,
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        console.error('[useGroupChat] Delete message error:', error);
        throw error;
      }
    },
    [user]
  );

  /**
   * Add a reaction to a message
   */
  const addReaction = useCallback(
    async (messageId: string, emoji: string) => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const firebaseUid = auth.currentUser?.uid;

      if (!firebaseUid) {
        throw new Error('Firebase user not authenticated');
      }

      // Optimistic update
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;

          const currentReactors = m.reactions[emoji] || [];
          if (currentReactors.includes(firebaseUid)) {
            return m;
          }

          return {
            ...m,
            reactions: {
              ...m.reactions,
              [emoji]: [...currentReactors, firebaseUid],
            },
            reactionCounts: {
              ...m.reactionCounts,
              [emoji]: (m.reactionCounts[emoji] || 0) + 1,
            },
            updatedAt: new Date().toISOString(),
          };
        })
      );

      try {
        const messageRef = doc(db, MESSAGES_COLLECTION, messageId);
        await updateDoc(messageRef, {
          [`reactions.${emoji}`]: arrayUnion(firebaseUid),
          [`reactionCounts.${emoji}`]: increment(1),
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        console.error('[useGroupChat] Add reaction error:', error);
        throw error;
      }
    },
    [user]
  );

  /**
   * Remove a reaction from a message
   */
  const removeReaction = useCallback(
    async (messageId: string, emoji: string) => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const firebaseUid = auth.currentUser?.uid;

      if (!firebaseUid) {
        throw new Error('Firebase user not authenticated');
      }

      // Optimistic update
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;

          const currentReactors = m.reactions[emoji] || [];
          if (!currentReactors.includes(firebaseUid)) {
            return m;
          }

          const newReactors = currentReactors.filter((id) => id !== firebaseUid);
          const newCount = Math.max(0, (m.reactionCounts[emoji] || 1) - 1);

          const newReactions = { ...m.reactions };
          const newCounts = { ...m.reactionCounts };

          if (newReactors.length === 0) {
            delete newReactions[emoji];
            delete newCounts[emoji];
          } else {
            newReactions[emoji] = newReactors;
            newCounts[emoji] = newCount;
          }

          return {
            ...m,
            reactions: newReactions,
            reactionCounts: newCounts,
            updatedAt: new Date().toISOString(),
          };
        })
      );

      try {
        const messageRef = doc(db, MESSAGES_COLLECTION, messageId);
        await updateDoc(messageRef, {
          [`reactions.${emoji}`]: arrayRemove(firebaseUid),
          [`reactionCounts.${emoji}`]: increment(-1),
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        console.error('[useGroupChat] Remove reaction error:', error);
        throw error;
      }
    },
    [user]
  );

  /**
   * Pin / unpin a message (web addition — leaders only; Firestore rules
   * enforce isGroupLeader and restrict the diff to pin fields).
   */
  const setPinned = useCallback(
    async (messageId: string, pinned: boolean) => {
      const firebaseUid = auth.currentUser?.uid;
      if (!firebaseUid) throw new Error('Firebase user not authenticated');

      // Optimistic update
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, isPinned: pinned } : m))
      );

      try {
        const messageRef = doc(db, MESSAGES_COLLECTION, messageId);
        await updateDoc(messageRef, {
          isPinned: pinned,
          pinnedAt: pinned ? serverTimestamp() : null,
          pinnedBy: pinned ? firebaseUid : null,
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        setMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, isPinned: !pinned } : m))
        );
        console.error('[useGroupChat] Pin toggle error:', error);
        throw error;
      }
    },
    []
  );

  /**
   * Set current user's typing status
   */
  const setTyping = useCallback(
    (isTyping: boolean) => {
      if (!user || !groupId) return;

      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
      }

      typingDebounceRef.current = setTimeout(() => {
        if (isTyping === isTypingRef.current) return;

        isTypingRef.current = isTyping;

        if (isTyping) {
          console.log('[useGroupChat] Typing started:', { groupId, userId: user.id });

          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            isTypingRef.current = false;
            console.log('[useGroupChat] Typing auto-stopped (timeout)');
          }, TYPING_TIMEOUT_MS);
        } else {
          console.log('[useGroupChat] Typing stopped:', { groupId, userId: user.id });

          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
          }
        }
      }, TYPING_DEBOUNCE_MS);
    },
    [user, groupId]
  );

  /**
   * Handle incoming typing event from another user
   */
  const handleRemoteTyping = useCallback(
    (typingUser: TypingUser, isTyping: boolean) => {
      if (!mountedRef.current) return;

      if (user && typingUser.userId === user.id) return;

      if (isTyping) {
        setTypingUsers((prev) => {
          const exists = prev.find((u) => u.userId === typingUser.userId);
          if (exists) {
            return prev.map((u) =>
              u.userId === typingUser.userId ? { ...u, startedAt: Date.now() } : u
            );
          }
          return [...prev, { ...typingUser, startedAt: Date.now() }];
        });

        const existingTimeout = typingUsersTimeoutRef.current.get(typingUser.userId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        const timeout = setTimeout(() => {
          setTypingUsers((prev) => prev.filter((u) => u.userId !== typingUser.userId));
          typingUsersTimeoutRef.current.delete(typingUser.userId);
        }, TYPING_TIMEOUT_MS);

        typingUsersTimeoutRef.current.set(typingUser.userId, timeout);
      } else {
        setTypingUsers((prev) => prev.filter((u) => u.userId !== typingUser.userId));

        const existingTimeout = typingUsersTimeoutRef.current.get(typingUser.userId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          typingUsersTimeoutRef.current.delete(typingUser.userId);
        }
      }
    },
    [user]
  );

  return {
    setPinned,
    messages: displayedMessages,
    pinnedMessages,
    loading,
    hasMore: isScrollContextActive ? false : hasMore,
    isScrollContextActive,
    clearScrollContext,
    loadMore,
    sendMessage,
    editMessage,
    deleteMessage,
    addReaction,
    removeReaction,
    typingUsers,
    setTyping,
    isSyncing,
    pendingCount,
    error,
    _handleRemoteTyping: handleRemoteTyping,
  };
}

export default useGroupChat;
